// pages/EditProfile.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  FiUser, 
  FiMapPin, 
  FiInstagram,
  FiTwitter,
  FiLinkedin,
  FiGlobe,
  FiCamera,
  FiX,
  FiCheck,
  FiChevronDown,
  FiCalendar
} from 'react-icons/fi';
import DashboardLayout from '../components/DashboardLayout';
import LocationSelector from '../components/LocationSelector';
import GenderSelector from '../components/GenderSelector';
import { useAuth } from '../services/auth';
import { 
  getCurrentUserProfile, 
  updateUserProfile, 
  uploadProfilePhoto,
  deleteProfilePhoto 
} from '../services/userService';
import { 
  checkUsernameAvailability, 
  updateUsername 
} from '../services/usernameService';
import '../styles/EditProfile.scss';

const EditProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Profile data
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [originalUsername, setOriginalUsername] = useState('');
  const [bio, setBio] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  // Demographics
  const [gender, setGender] = useState('');
  const [birthday, setBirthday] = useState('');
  const [showGenderSelector, setShowGenderSelector] = useState(false);
  
  // Location
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [country, setCountry] = useState('');
  const [countryId, setCountryId] = useState(null);
  const [stateId, setStateId] = useState(null);
  const [, setCityId] = useState(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const [locationSelectorType, setLocationSelectorType] = useState('country');
  
  // Social links
  const [instagram, setInstagram] = useState('');
  const [twitter, setTwitter] = useState('');
  const [linkedin, setLinkedin] = useState('');
  const [website, setWebsite] = useState('');
  
  // Validation
  const [usernameError, setUsernameError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const profile = await getCurrentUserProfile();
      if (profile) {
        setFirstName(profile.first_name || '');
        setLastName(profile.last_name || '');
        setUsername(profile.username || '');
        setOriginalUsername(profile.username || '');
        setBio(profile.bio || '');
        setGender(profile.gender || '');
        setBirthday(profile.birthday || '');
        
        setCity(profile.city || '');
        setState(profile.state || '');
        setCountry(profile.country || '');
        
        setPhotoPreview(profile.profile_photo_cdn_url);
        
        if (profile.social_links) {
          setInstagram(profile.social_links.instagram || '');
          setTwitter(profile.social_links.twitter || '');
          setLinkedin(profile.social_links.linkedin || '');
          setWebsite(profile.social_links.website || '');
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUsernameChange = async (value) => {
    const cleaned = value.toLowerCase().replace(/[^a-z0-9_]/g, '');
    setUsername(cleaned);
    setHasChanges(true);
    
    if (cleaned && cleaned !== originalUsername) {
      try {
        const available = await checkUsernameAvailability(cleaned);
        if (!available) {
          setUsernameError('This username is already taken');
        } else {
          setUsernameError('');
        }
      } catch (error) {
        console.error('Error checking username:', error);
      }
    } else {
      setUsernameError('');
    }
  };

  const handlePhotoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      
      setProfilePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemovePhoto = () => {
    if (window.confirm('Are you sure you want to remove your profile photo?')) {
      setProfilePhoto(null);
      setPhotoPreview(null);
      setHasChanges(true);
    }
  };

  const handleLocationSelect = (data) => {
    setCountry(data.country);
    setCountryId(data.countryId);
    
    if (data.state) {
      setState(data.state);
      setStateId(data.stateId);
    }
    
    if (data.city) {
      setCity(data.city);
      setCityId(data.cityId);
    }
    
    setShowLocationSelector(false);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      alert('First and last name are required');
      return;
    }

    if (usernameError) {
      alert('Please fix the username error before saving');
      return;
    }

    setSaving(true);

    try {
      // Upload photo if changed. Both calls save the users row themselves
      // (profile_photo_cdn_url), so nothing below needs the URL.
      if (profilePhoto) {
        setUploadingPhoto(true);
        await uploadProfilePhoto(profilePhoto);
        setUploadingPhoto(false);
      } else if (!photoPreview) {
        // Photo was removed
        await deleteProfilePhoto();
      }

      // Prepare social links
      const socialLinks = {};
      if (instagram.trim()) socialLinks.instagram = instagram.trim();
      if (twitter.trim()) socialLinks.twitter = twitter.trim();
      if (linkedin.trim()) socialLinks.linkedin = linkedin.trim();
      if (website.trim()) socialLinks.website = website.trim();

      // Update profile
      const profileUpdates = {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        username: username.trim() || null,
        display_name: `${firstName.trim()} ${lastName.trim()}`,
        bio: bio.trim() || null,
        gender: gender || null,
        birthday: birthday || null,
        city: city.trim() || null,
        state: state.trim() || null,
        country: country.trim() || null,
        social_links: Object.keys(socialLinks).length > 0 ? socialLinks : null,
      };

      await updateUserProfile(profileUpdates);

      // Update username if changed
      if (username && username !== originalUsername) {
        await updateUsername(username, user.id, originalUsername);
      }

      alert('Profile updated successfully!');
      setHasChanges(false);
      navigate(-1);
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
      setUploadingPhoto(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to go back?')) {
        navigate(-1);
      }
    } else {
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="edit-profile-page">
        <div className="edit-profile-container">
          <motion.div 
            className="edit-profile-card glass-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="edit-profile-header">
              <h1>Edit Profile</h1>
              <div className="header-actions">
                <button 
                  className="btn btn-secondary"
                  onClick={handleCancel}
                  disabled={saving}
                >
                  <FiX />
                  Cancel
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={handleSave}
                  disabled={saving || !hasChanges || usernameError}
                >
                  {saving ? (
                    <div className="spinner-small"></div>
                  ) : (
                    <>
                      <FiCheck />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Profile Photo */}
            <div className="profile-photo-section">
              <div className="photo-container">
                {photoPreview ? (
                  <img src={photoPreview} alt="Profile" className="profile-photo" />
                ) : (
                  <div className="photo-placeholder">
                    <FiUser size={48} />
                  </div>
                )}
                {uploadingPhoto && (
                  <div className="photo-uploading">
                    <div className="spinner-small"></div>
                  </div>
                )}
              </div>
              <div className="photo-actions">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />
                <button
                  className="btn btn-outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  <FiCamera />
                  {photoPreview ? 'Change Photo' : 'Upload Photo'}
                </button>
                {photoPreview && (
                  <button
                    className="btn btn-outline btn-danger"
                    onClick={handleRemovePhoto}
                    disabled={uploadingPhoto}
                  >
                    <FiX />
                    Remove
                  </button>
                )}
              </div>
            </div>

            {/* Basic Info */}
            <div className="form-section">
              <h3>Basic Info</h3>
              
              <div className="form-group">
                <label>Username</label>
                <div className="input-with-icon">
                  <span className="input-prefix">@</span>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => handleUsernameChange(e.target.value)}
                    placeholder="username"
                    className={usernameError ? 'error' : ''}
                  />
                </div>
                {usernameError && <span className="error-text">{usernameError}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={firstName}
                    onChange={(e) => {
                      setFirstName(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="First name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={lastName}
                    onChange={(e) => {
                      setLastName(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Last name"
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => {
                    setBio(e.target.value);
                    setHasChanges(true);
                  }}
                  placeholder="Tell us about yourself..."
                  rows={4}
                  maxLength={200}
                />
                <span className="char-count">{bio.length}/200</span>
              </div>
            </div>

            {/* Demographics */}
            <div className="form-section">
              <h3>Demographics</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Birthday</label>
                  <div className="input-with-icon">
                    <FiCalendar className="input-icon" />
                    <input
                      type="date"
                      value={birthday}
                      onChange={(e) => {
                        setBirthday(e.target.value);
                        setHasChanges(true);
                      }}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Gender</label>
                  <button
                    className="select-button"
                    onClick={() => setShowGenderSelector(true)}
                  >
                    <span>{gender || 'Select gender'}</span>
                    <FiChevronDown />
                  </button>
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="form-section">
              <h3>Location</h3>
              
              <div className="form-group">
                <label>Country</label>
                <button
                  className="select-button"
                  onClick={() => {
                    setLocationSelectorType('country');
                    setShowLocationSelector(true);
                  }}
                >
                  <FiMapPin className="input-icon" />
                  <span>{country || 'Select country'}</span>
                  <FiChevronDown />
                </button>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>State</label>
                  <button
                    className="select-button"
                    onClick={() => {
                      setLocationSelectorType('state');
                      setShowLocationSelector(true);
                    }}
                    disabled={!countryId}
                  >
                    <span>{state || 'Select state'}</span>
                    <FiChevronDown />
                  </button>
                </div>

                <div className="form-group">
                  <label>City</label>
                  <button
                    className="select-button"
                    onClick={() => {
                      setLocationSelectorType('city');
                      setShowLocationSelector(true);
                    }}
                    disabled={!stateId}
                  >
                    <span>{city || 'Select city'}</span>
                    <FiChevronDown />
                  </button>
                </div>
              </div>
            </div>

            {/* Social Links */}
            <div className="form-section">
              <h3>Social Links</h3>
              
              <div className="form-group">
                <label>Instagram</label>
                <div className="input-with-icon">
                  <FiInstagram className="input-icon" />
                  <input
                    type="text"
                    value={instagram}
                    onChange={(e) => {
                      setInstagram(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Instagram username"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Twitter</label>
                <div className="input-with-icon">
                  <FiTwitter className="input-icon" />
                  <input
                    type="text"
                    value={twitter}
                    onChange={(e) => {
                      setTwitter(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="Twitter username"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>LinkedIn</label>
                <div className="input-with-icon">
                  <FiLinkedin className="input-icon" />
                  <input
                    type="text"
                    value={linkedin}
                    onChange={(e) => {
                      setLinkedin(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="LinkedIn profile"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Website</label>
                <div className="input-with-icon">
                  <FiGlobe className="input-icon" />
                  <input
                    type="url"
                    value={website}
                    onChange={(e) => {
                      setWebsite(e.target.value);
                      setHasChanges(true);
                    }}
                    placeholder="https://yourwebsite.com"
                  />
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modals */}
      {showGenderSelector && (
        <GenderSelector
          currentValue={gender}
          onSelect={(value) => {
            setGender(value);
            setShowGenderSelector(false);
            setHasChanges(true);
          }}
          onClose={() => setShowGenderSelector(false)}
        />
      )}

      {showLocationSelector && (
        <LocationSelector
          type={locationSelectorType}
          countryId={countryId}
          stateId={stateId}
          currentValue={
            locationSelectorType === 'country' ? country :
            locationSelectorType === 'state' ? state : city
          }
          onSelect={handleLocationSelect}
          onClose={() => setShowLocationSelector(false)}
        />
      )}
    </DashboardLayout>
  );
};

export default EditProfile;